import { useEffect, useRef, useState } from 'react';
import purdueLogo from '@/assets/logos/purdue.svg';
import mtsuLogo from '@/assets/logos/mtsu.png';
import ekuLogo from '@/assets/logos/eku.png';
import learnupLogo from '@/assets/logos/learnup.png';

type LogoItem = {
  name: string;
  src: string;
  /** Optical height class — normalizes visual weight across very different lockups */
  className: string;
  /**
   * Some marks (e.g. Purdue's two-tone P) lose their internal counters when
   * flattened to a single ink, so they keep tonal separation instead.
   */
  tonal?: boolean;
};

const LOGOS: LogoItem[] = [
  { name: 'Purdue University', src: purdueLogo, className: 'h-6 sm:h-7', tonal: true },
  { name: 'Middle Tennessee State University', src: mtsuLogo, className: 'h-6 sm:h-7' },
  { name: 'Eastern Kentucky University', src: ekuLogo, className: 'h-8 sm:h-10' },
  { name: 'LearnUp Academy', src: learnupLogo, className: 'h-11 sm:h-14' },
];

const BASE_TREATMENT =
  'w-auto object-contain opacity-60 transition-[opacity,filter] duration-500 ' +
  'hover:opacity-100 hover:[filter:none] dark:hover:[filter:none]';

/** Flattened to a single ink — crisp for solid, single-color marks. */
const FLAT_FILTER =
  '[filter:grayscale(1)_brightness(0)] dark:[filter:grayscale(1)_brightness(0)_invert(1)]';

/** Keeps light/dark tonal steps inside the mark so counters stay visible. */
const TONAL_FILTER =
  '[filter:grayscale(1)_contrast(1.15)_brightness(0.5)] ' +
  'dark:[filter:grayscale(1)_invert(1)_contrast(1.1)_brightness(1.15)]';

const TRACK_COPIES = 4;
/** px per second of idle drift — matches the previous 60s loop feel. */
const IDLE_SPEED = 40;

const LogoTrack = ({
  ariaHidden,
  trackRef,
}: {
  ariaHidden?: boolean;
  trackRef?: (el: HTMLUListElement | null) => void;
}) => (
  <ul
    ref={trackRef}
    className="flex shrink-0 items-center gap-12 sm:gap-20 pr-12 sm:pr-20"
    aria-hidden={ariaHidden || undefined}
  >
    {LOGOS.map((logo) => (
      <li key={logo.name} className="flex items-center">
        <img
          src={logo.src}
          alt={ariaHidden ? '' : `${logo.name} logo`}
          loading="lazy"
          decoding="async"
          draggable={false}
          className={`${logo.className} ${BASE_TREATMENT} ${logo.tonal ? TONAL_FILTER : FLAT_FILTER} select-none`}
        />
      </li>
    ))}
  </ul>
);

const LogoMarquee = () => {
  const beltRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement | null>(null);

  const offset = useRef(0);
  const trackWidth = useRef(0);
  const velocity = useRef(0); // px/s, from flicks
  const dragging = useRef(false);
  const pointerId = useRef<number | null>(null);
  const lastX = useRef(0);
  const lastT = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const belt = beltRef.current;
    if (!belt) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const measure = () => {
      trackWidth.current = trackRef.current?.getBoundingClientRect().width ?? 0;
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);

    let raf = 0;
    let prev = performance.now();

    const frame = (now: number) => {
      const dt = Math.min((now - prev) / 1000, 0.05);
      prev = now;

      if (!dragging.current) {
        if (Math.abs(velocity.current) > 1) {
          offset.current += velocity.current * dt;
          velocity.current *= Math.pow(0.94, dt * 60); // friction
        } else {
          velocity.current = 0;
          if (!reduce) offset.current -= IDLE_SPEED * dt;
        }
      }

      const w = trackWidth.current;
      if (w > 0) {
        offset.current = ((offset.current % w) + w) % w;
        belt.style.transform = `translate3d(${offset.current - w}px, 0, 0)`;
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    pointerId.current = e.pointerId;
    lastX.current = e.clientX;
    lastT.current = performance.now();
    velocity.current = 0;
    setIsDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current || pointerId.current !== e.pointerId) return;
    const now = performance.now();
    const dx = e.clientX - lastX.current;
    const dt = Math.max((now - lastT.current) / 1000, 0.001);
    offset.current += dx;
    velocity.current = dx / dt;
    lastX.current = e.clientX;
    lastT.current = now;
  };

  const endDrag = (e: React.PointerEvent) => {
    if (pointerId.current !== e.pointerId) return;
    dragging.current = false;
    pointerId.current = null;
    setIsDragging(false);
  };

  return (
    <section className="relative py-14 sm:py-16" aria-label="Trusted by">
      <div className="container mx-auto px-4 sm:px-6">
        <div
          className={`relative mt-8 sm:mt-10 overflow-hidden touch-pan-y select-none ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{
            maskImage:
              'linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0, black 8%, black 92%, transparent 100%)',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {/*
            Drag-and-flick belt. Copies of the set repeat so the strip always
            overflows the viewport in both directions; the transform wraps by
            one track width so the loop is seamless.
          */}
          <div ref={beltRef} className="flex w-max will-change-transform">
            {Array.from({ length: TRACK_COPIES }).map((_, i) => (
              <LogoTrack
                key={i}
                ariaHidden={i !== 0}
                trackRef={i === 0 ? (el) => (trackRef.current = el) : undefined}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LogoMarquee;
